import type { Id } from '@/convex/_generated/dataModel';

export const WORKSPACE_NODE_REFERENCE_VERSION = 1 as const;

export type WorkspaceNodeReferenceKind = 'file' | 'directory';

/**
 * A durable pointer to a VFS node. `pathHint` is presentation-only and must
 * never be used to resolve or authorize the referenced resource.
 */
export type WorkspaceNodeReference = {
  version: typeof WORKSPACE_NODE_REFERENCE_VERSION;
  kind: WorkspaceNodeReferenceKind;
  vfsNodeId: Id<'vfsNodes'>;
  pathHint?: string;
};

const WORKSPACE_NODE_REFERENCE_KEYS = new Set([
  'version',
  'kind',
  'vfsNodeId',
  'pathHint',
]);

export function parseWorkspaceNodeReference(
  value: unknown
): WorkspaceNodeReference | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }

  const candidate = value as Record<string, unknown>;
  if (
    Object.keys(candidate).some(
      (key) => !WORKSPACE_NODE_REFERENCE_KEYS.has(key)
    ) ||
    candidate.version !== WORKSPACE_NODE_REFERENCE_VERSION ||
    (candidate.kind !== 'file' && candidate.kind !== 'directory') ||
    typeof candidate.vfsNodeId !== 'string' ||
    candidate.vfsNodeId.length === 0 ||
    (candidate.pathHint !== undefined && typeof candidate.pathHint !== 'string')
  ) {
    return null;
  }

  return {
    version: WORKSPACE_NODE_REFERENCE_VERSION,
    kind: candidate.kind,
    vfsNodeId: candidate.vfsNodeId as Id<'vfsNodes'>,
    ...(candidate.pathHint !== undefined
      ? { pathHint: candidate.pathHint }
      : {}),
  };
}

export function workspaceNodeReferenceFromEntry(args: {
  kind: WorkspaceNodeReferenceKind;
  vfsNodeId?: Id<'vfsNodes'>;
  path?: string;
}): WorkspaceNodeReference | null {
  if (!args.vfsNodeId) return null;
  return {
    version: WORKSPACE_NODE_REFERENCE_VERSION,
    kind: args.kind,
    vfsNodeId: args.vfsNodeId,
    ...(args.path !== undefined ? { pathHint: args.path } : {}),
  };
}

export function parseWorkspaceNodeReferences(
  values: readonly unknown[]
): WorkspaceNodeReference[] | null {
  const references: WorkspaceNodeReference[] = [];
  for (const value of values) {
    const reference = parseWorkspaceNodeReference(value);
    if (!reference) return null;
    references.push(reference);
  }
  return references;
}
